import { ThemeOptions } from '@mui/material';

import { tokens } from '../../../theme/generated/tokens';
import { DsShadow } from '../../../theme/shadows';
import { AbsolutePseudo, focusProps } from '../../../theme/utils';
import { cssVars } from '../../../utils/cssVars';

const { setValues, getters } = cssVars({
  color: tokens.theme.fg.selection.primary,
  fontSize: '14px',
  lineHeight: '22px',
  bottomPadding: '8px',
});

export const tabsTheme: ThemeOptions['components'] = {
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 0,
        width: 'max-content',
        '& .MuiTabs-scrollButtons': {
          paddingBottom: '8px',

          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
        '&.DS-Tabs-fullWidth': {
          width: 'initial',
        },
        ...setValues({
          color: tokens.theme.fg.selection.primary,
        }),
        '&.DS-Tabs-size': {
          '&Small': setValues({
            fontSize: '12px',
            lineHeight: '18px',
            bottomPadding: '4px',
          }),
          '&Medium': setValues({
            fontSize: '14px',
            lineHeight: '22px',
            bottomPadding: '8px',
          }),
        },
        '&.DS-Tabs-variant': {
          '&Scrollable': {
            width: 'max-content',
            maxWidth: '100%',
          },
          '&NoUnderline': {
            '& .MuiTabs-flexContainer': {
              borderBottom: 'none',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          },
          '&Card': {
            '& .MuiTab-root': {
              backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
              boxShadow: theme.shadows[DsShadow.SHADOW],
              borderRadius: '4px 4px 0px 0px',
              clipPath: 'inset(-4px -4px 0px -4px)',
              padding: '4px 8px',
              '&.Mui-selected': {
                backgroundColor: tokens.theme.bg.structural.surface,
              },
              '&.Mui-disabled': {
                backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
                color: tokens.theme.disabled.fg,
                '&.Mui-selected': {
                  backgroundColor: tokens.theme.bg.structural.surface,
                  color: tokens.theme.disabled.fg,
                },
              },
            },

            '& .MuiTabs-scroller': {
              overflow: 'visible !important',
            },
            '& .MuiTabs-flexContainer': {
              borderBottom: 'none',
              gap: '4px',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          },
        },
        '&.DS-Tabs-activeTabHover': setValues({
          color: tokens.theme.fg.selection.primaryHover,
        }),
      }),
      flexContainer: () => ({
        borderBottom: `1px solid ${tokens.theme.border.structural.neutral}`,
        gap: '32px',
      }),
      indicator: {
        height: '1px',
        backgroundColor: getters.color(),
        borderRadius: tokens.theme.radius.sm,
      },
      scroller: {
        height: 'max-content',
      },
    },
  },
  MuiTab: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: ({ theme }) => ({
        textTransform: 'none',
        minHeight: 'unset',
        minWidth: 'unset',
        padding: '0 4px',
        paddingBottom: getters.bottomPadding(),
        ...theme.typography.P14M,
        color: tokens.theme.fg.structural.secondary,
        fontSize: getters.fontSize(),
        lineHeight: getters.lineHeight(),

        '&:hover': {
          overflow: 'visible',
          '&:not(.Mui-selected)': {
            color: tokens.theme.fg.action.linkNeutralHover,
          },
        },
        '&.Mui-selected': {
          color: getters.color(),
          fontWeight: 600,
        },
        '&.Mui-disabled': {
          color: tokens.theme.disabled.fg,
          '&.Mui-selected': {
            color: tokens.theme.disabled.fg,
          },
        },
        '& .DsTab-Label': {
          paddingLeft: '4px',
          paddingRight: '4px',
        },
        '&.Mui-focusVisible': {
          '& .DsTab-Label': {
            position: 'relative',
            '&:after': {
              ...AbsolutePseudo,
              ...focusProps(),
              borderRadius: tokens.theme.radius.sm,
            },
          },
        },
      }),
    },
  },
};
